// @flow
import React from 'react'
import Immutable from 'seamless-immutable'

// core
import { getAssociatedFields } from '../SpecificationFunctions'
import { isConditionTrue } from '../ContentFunctions'

// components
import { FieldLabel, Popover, NumberInputArrayType, EmptyComponent } from '../../Components/'
import FormFields, {
  fieldsClasses,
  helpClasses,
  labelClasses,
  formGroupClasses,
} from '../../Components/_formFields/fields'
import miniAppsComponents from '../../Components/_miniApps'

// types
import type { ProductContent, ContentComponentType } from '../Types/ProductContent'
import type { ExpandedSpecificationDTO, FieldDTO } from '../Types/Field'
import type { InputHelpsType } from '../Types/InputHelpType'
import type { FormValueType, ValueObject } from '../Types/Values'
import type { ArrayFieldFormTypes } from '../Types/ArrayFieldFormType'
import type { ResourcesType } from '../Types/Resources'
import type { ArrayFieldValidationError } from '../Types/ValidationError'
import type { ActiveSettingsType } from '../Types/SettingsTypes'

export type FieldFormProps = {
  activeSettings: ActiveSettingsType,
  addNewItemToArrayType: (key: string, path: string) => void,
  arrayTypes: ArrayFieldFormTypes,
  blockClassName: string,
  blockClassNameForR: string,
  blockContentClassName: string,
  content: ProductContent,
  data: { [key: string]: any },
  debug?: boolean,
  fieldsClasses?: Object,
  formData: { [key: string]: any },
  formGroupClassName?: string,
  helpClasses?: Object,
  hideFirstFormOfArrayType?: boolean,
  id: string,
  inputHelps?: InputHelpsType,
  internallyHiddenFields: { [fieldPath: string]: boolean },
  isBackOffice: boolean,
  labelClasses?: Object,
  labelValue?: string,
  miniAppData?: Object,
  miniApps?: Object,
  onBlur?: (key: string, value: any, paths: Array<string | number>, changed?: boolean) => void,
  onChangeFormData: (formData: Object, key?: string[], value?: any) => void,
  onChangeValue: (key: string, value: FormValueType, pathArray?: Array<string | number>) => void,
  prefix: string,
  prefixArray: Array<string | number>,
  pushIsPristine: boolean,
  removeItemFromArrayType: (key: string, path: string) => void,
  renderBeforeField?: (fieldPath: string) => any,
  resources?: ResourcesType,
  setInternallyHiddenFields?: (path: string, value: boolean) => void,
  setNumberOfItemsInArrayType: (string, string, number) => void,
  showArrayTypeInput?: boolean,
  showArrayTypeTitle?: boolean,
  showBlockTitle?: boolean,
  specification: ExpandedSpecificationDTO,
  tabKey?: string,
  tabs?: boolean,
  templateVariables?: ValueObject,
  title?: string,
  token: string,
  validationErrors: { [key: string]: { [key: string]: string } },
  valueNames: { [key: string]: { [key: string]: string } },
}

export type FieldFormState = {
  associatedFields: any,
  rawContent: ProductContent,
  rawSpecification: ExpandedSpecificationDTO,
  specification: any,
}

export default class FieldForm extends React.Component<FieldFormProps, FieldFormState> {
  static defaultProps = {
    activeSettings: {},
    addNewItemToArrayType: () => null,
    arrayTypes: {},
    blockClassName: 'card card-lg mx-auto bg-transparent border-0 border-sm-default mt-5',
    blockClassNameForR: 'card card-lg mx-auto bg-transparent border-0 border-sm-default mt-5',
    blockContentClassName: 'card-body px-0 px-sm-3',
    data: {},
    loadedData: {},
    debug: false,
    fieldsClasses: {},
    formData: {},
    formGroupClassName: 'form-group d-flex flex-row flex-wrap flex-sm-row align-items-top',
    helpClasses: {},
    hideFirstFormOfArrayType: false,
    id: '',
    inputHelps: {},
    isBackOffice: false,
    labelClasses: {},
    miniAppData: {},
    miniApps: {},
    onChangeValue: () => null,
    prefix: '',
    prefixArray: [],
    removeItemFromArrayType: () => null,
    resources: {},
    showArrayTypeInput: false,
    showArrayTypeTitle: false,
    showBlockTitle: true,
    tabKey: '',
    tabs: false,
    templateVariables: {},
    title: '',
  }

  static getDerivedStateFromProps = (nextProps: FieldFormProps, prevState: FieldFormState) => {
    let changed = false
    const newState = {}
    if (JSON.stringify(nextProps.content) !== JSON.stringify(prevState.rawContent)) {
      changed = true
      newState.rawContent = nextProps.content
      newState.associatedFields = getAssociatedFields(nextProps.content)
    }
    if (JSON.stringify(nextProps.specification) !== JSON.stringify(prevState.rawSpecification)) {
      changed = true
      newState.rawSpecification = nextProps.specification
      newState.specification = {
        ...nextProps.specification,
        fields: nextProps.specification.fields.reduce((acc, field) => {
          acc[field.name] = field
          return acc
        }, {}),
        types: nextProps.specification.types.reduce((acc, type) => {
          acc[type.name] = type
          return acc
        }, {}),
      }
    }
    return changed ? newState : null
  }

  state = {
    associatedFields: {},
    rawContent: {},
    rawSpecification: {},
    specification: {},
  }

  getErrorForArrayType = (validationErrors: Array<ArrayFieldValidationError>, index: number) => {
    if (validationErrors && Array.isArray(validationErrors)) {
      const errors = validationErrors.filter((error) => error.item_index === index)
      return errors.length ? errors[0].error : {}
    } else if (typeof validationErrors === 'object' && validationErrors[index]) {
      return validationErrors[index]
    }
    return {}
  }

  handleOnValueChange = (key: string, value: string | Object, pathArray: Array<string | number> = []) => {
    this.props.onChangeValue(key, value, [key, ...pathArray])
  }

  handleOnArrayValueChange = (
    arrayKey: string,
    arrayIndex: number,
    key: string,
    value: string | Object,
    pathArray: Array<string | number> = [],
  ) => {
    this.props.onChangeValue(arrayKey, value, [arrayKey, arrayIndex, ...pathArray])
  }

  handleSetNumberOfItemsInArrayType = (key: string, prefix: string, value: number) => {
    this.props.setNumberOfItemsInArrayType(key, prefix, value)
  }

  handleOnBlur = (key: string, value: any, paths: Array<string | number> = [], changed?: boolean) => {
    if (this.props.onBlur) {
      paths = [...paths]
      if (key) {
        paths.push(key)
      }
      this.props.onBlur('', value, paths, changed)
    }
  }

  handleAddAssociatedField = (
    component: ContentComponentType,
    fieldSpecification: FieldDTO<ExpandedSpecificationDTO>,
    content: ProductContent,
  ): boolean | string => {
    let associatedFields = Immutable({ ...this.state.associatedFields[component.parent || 'defaultAssociation'] })
    let renderedName = associatedFields.rendered
    if (associatedFields.rendered === fieldSpecification.name && !fieldSpecification.enabled) {
      renderedName = false
    } else if (!renderedName && fieldSpecification.enabled) {
      renderedName = fieldSpecification.name
    }
    const itemFiltered = this.state.associatedFields[component.parent || 'defaultAssociation'].items.filter(
      (item) => item.name === fieldSpecification.name,
    )
    if (fieldSpecification.enabled && itemFiltered.length === 0) {
      if (!associatedFields.icons) {
        associatedFields = associatedFields.set('icons', {}).set('labels', {})
      }
      associatedFields = associatedFields.setIn(['icons', fieldSpecification.name], component.icon)
      associatedFields = associatedFields
        .set('rendered', renderedName)
        .setIn(['labels', fieldSpecification.name], content.fieldNames[fieldSpecification.name])
        .set(
          'items',
          associatedFields.items.length === 0
            ? [fieldSpecification]
            : associatedFields.items.flatMap((value, index) => {
                if (index === associatedFields.items.length - 1) {
                  return [value, fieldSpecification]
                }
                return value
              }),
        )
      this.setState({
        associatedFields: {
          ...this.state.associatedFields,
          [component.parent || 'defaultAssociation']: associatedFields.asMutable(),
        },
      })
    } else if (
      itemFiltered.length &&
      (associatedFields.rendered !== renderedName || itemFiltered[0].enabled !== fieldSpecification.enabled)
    ) {
      this.setState({
        associatedFields: {
          ...this.state.associatedFields,
          [component.parent || 'defaultAssociation']: associatedFields
            .set('rendered', renderedName)
            .set(
              'items',
              associatedFields.items.flatMap((item) => {
                if (item.name === fieldSpecification.name) {
                  return item.set('enabled', fieldSpecification.enabled)
                }
                return item
              }),
            )
            .asMutable(),
        },
      })
    }
    return renderedName
  }

  handleChangeFormData = (formData: Object, keys?: Array<string | number>) => {
    this.props.onChangeFormData(formData, keys)
  }

  handleChangeFieldVisibility = (fieldPath: string, hidden: boolean) => {
    if (this.props.setInternallyHiddenFields) {
      this.props.setInternallyHiddenFields(fieldPath, hidden)
    } else {
      this.setState({ [`isHidden${fieldPath}`]: hidden })
    }
  }

  render() {
    const {
      content,
      tabs,
      tabKey,
      validationErrors,
      arrayTypes,
      debug,
      data,
      loadedData,
      id,
      inputHelps,
      pushIsPristine,
      resources,
      templateVariables,
      valueNames,
      prefix,
      labelValue,
      showArrayTypeTitle,
      blockClassName,
      showArrayTypeInput,
      hideFirstFormOfArrayType,
      miniApps,
      isBackOffice,
      formData,
      miniAppData,
      blockContentClassName,
      blockClassNameForR,
      showBlockTitle,
      token,
      renderBeforeField,
      activeSettings,
      prefixArray,
    } = this.props
    const { setNumberOfItemsInArrayType } = this.props
    const { specification, associatedFields } = this.state

    let formGroupClassName = this.props.formGroupClassName

    if (!content || !content.fieldOrder || !specification || !specification.fields) {
      return null
    }

    const fieldsClassesForm = { ...fieldsClasses, ...this.props.fieldsClasses }
    const helpClassesForm = { ...helpClasses, ...this.props.helpClasses }
    const labelClassesForm = { ...labelClasses, ...this.props.labelClasses }

    return content.fieldOrder.map<any>((fieldName) => {
      let miniAppsArray = []
      let miniAppInputHelps = [{}]
      let miniAppKey = `${prefix}${fieldName}`
      if (content.components && content.components[fieldName] && content.components[fieldName].associated) {
        miniAppKey = `${prefix}${content.components[fieldName].parent}`
      }
      if (miniApps && miniApps[miniAppKey]) {
        if (Array.isArray(miniApps[miniAppKey])) {
          miniAppsArray = []
          miniAppInputHelps = []
          miniApps[miniAppKey].forEach((miniKey) => {
            if (miniAppsComponents[miniKey]) {
              miniAppsArray.push(miniAppsComponents[miniKey])
            }
            // $FlowIssue
            if (inputHelps[miniKey]) {
              miniAppInputHelps.push(inputHelps[miniKey])
            }
          })
        } else if (miniAppsComponents[miniApps[miniAppKey]]) {
          miniAppsArray = [miniAppsComponents[miniApps[miniAppKey]]]
          // $FlowIssue
          miniAppInputHelps = [inputHelps[miniApps[miniAppKey]]]
        }
      }

      if (
        specification.fields[fieldName] &&
        specification.fields[fieldName].domain._type === 'object' &&
        specification.fields[fieldName].domain.specification.name &&
        (!tabs || (tabs && tabKey === fieldName))
      ) {
        const fieldSpecification = specification.fields[fieldName].domain.specification

        if (!specification.fields[fieldName].enabled || this.state[`isHidden${fieldName}`]) {
          return null
        }

        let additionalProps = {}
        let Component = FieldForm
        if (content.components && (content.components[fieldName] || content.components[fieldSpecification.name])) {
          let component = (content.components[fieldSpecification.name] || content.components[fieldName]).name
          if (FormFields[component]) {
            Component = FormFields[component]
            additionalProps = { ...additionalProps, ...content.components[fieldName] }
            delete additionalProps.name
          }
        }

        const contentComponent = (
          <>
            <Component
              activeSettings={activeSettings}
              arrayTypes={arrayTypes}
              blockClassName={blockClassNameForR}
              blockClassNameForR={blockClassNameForR}
              blockContentClassName={blockContentClassName}
              content={content.types[fieldSpecification.name]}
              data={data[fieldName]}
              debug={debug}
              fieldsClasses={this.props.fieldsClasses}
              formData={formData}
              formGroupClassName={formGroupClassName}
              helpClasses={this.props.helpClasses}
              hideFirstFormOfArrayType={hideFirstFormOfArrayType}
              id={id}
              inputHelps={inputHelps[fieldName] || {}}
              internallyHiddenFields={this.props.internallyHiddenFields}
              isBackOffice={isBackOffice}
              key={`field-form-${fieldName}`}
              labelClasses={this.props.labelClasses}
              loadedData={loadedData[fieldName]}
              miniAppData={miniAppData}
              miniApps={miniApps}
              onBlur={(_, value, paths, changed) => {
                this.handleOnBlur(fieldName, value, paths, changed)
              }}
              onChangeFormData={this.props.onChangeFormData}
              onChangeValue={(key, value, pathArray: Array<string | number> = []) => {
                this.handleOnValueChange(fieldName, value, pathArray)
              }}
              prefix={`${fieldName}-`}
              prefixArray={[...prefixArray, fieldName]}
              pushIsPristine={pushIsPristine}
              resources={resources}
              setInternallyHiddenFields={this.handleChangeFieldVisibility}
              setNumberOfItemsInArrayType={setNumberOfItemsInArrayType}
              showArrayTypeInput={showArrayTypeInput}
              showArrayTypeTitle={showArrayTypeTitle}
              showBlockTitle={showBlockTitle}
              specification={fieldSpecification}
              templateVariables={templateVariables}
              title={content.fieldNames[fieldName]}
              token={token}
              validationErrors={validationErrors[fieldName] || {}}
              valueNames={valueNames}
              {...additionalProps}
            />
            {miniAppsArray.map((MiniApp, index) => (
              <MiniApp
                activeSettings={activeSettings}
                arrayTypes={arrayTypes}
                content={content.types[fieldSpecification.name]}
                formData={formData}
                fullContent={content}
                fullSpecification={this.props.specification}
                inputHelps={miniAppInputHelps[index]}
                isBackOffice={isBackOffice}
                key={`${prefix}${fieldName}-miniApp-${MiniApp.name || (MiniApp.type && MiniApp.type.name)}`}
                loadedData={loadedData}
                miniAppData={miniAppData}
                onChangeFieldVisibility={this.handleChangeFieldVisibility}
                onChangeFormData={this.handleChangeFormData}
                onChangeValue={this.props.onChangeValue}
                prefix={`${fieldName}-`}
                pushIsPristine={pushIsPristine}
                resources={resources}
                token={token}
                validationErrors={validationErrors}
              />
            ))}
          </>
        )

        if (content.types[fieldSpecification.name].hideCard) {
          return contentComponent
        }
        return (
          <div className={blockClassName} key={`card-${fieldName}`}>
            <div className={blockContentClassName}>
              {!tabs &&
                showBlockTitle && [
                  <div className="text-center" key={`title-${fieldName}`}>
                    <h2 id={fieldName}>{content.fieldNames[fieldName]}</h2>
                  </div>,
                  <hr className="border-0 border-sm-top border-active-object" key={`hr-${fieldName}`} />,
                ]}
              {contentComponent}
            </div>
          </div>
        )
      } else if (specification.fields[fieldName] && specification.fields[fieldName].domain._type === 'array') {
        const fieldSpecification = specification.fields[fieldName].domain.specification
        const arrayLength = arrayTypes[fieldName] ? arrayTypes[fieldName].length : 0
        const arrayToRender = []
        const arrayTitle = content.fieldNames[`${fieldName}ArrayTitle`] || `undefined: ${fieldName}-array-title`
        let validationErrorsArray = {}
        for (let arrayIndex = 0; arrayIndex < arrayLength; arrayIndex++) {
          validationErrorsArray = validationErrors[`${fieldName}`]
            ? this.getErrorForArrayType(validationErrors[`${fieldName}`], arrayIndex)
            : {}
          if ((hideFirstFormOfArrayType && arrayIndex !== 0) || !hideFirstFormOfArrayType) {
            arrayToRender.push([
              showArrayTypeTitle && (
                <div className="text-center" key={`field-form-array-${arrayIndex}-title`}>
                  <h2 id="insuredParties">{arrayTitle.replace('{value}', `${arrayIndex + 1}`)}</h2>
                </div>
              ),
              showArrayTypeTitle && (
                <hr className="border-0 border-sm-top border-active-object" key={`field-form-array-${arrayIndex}-hr`} />
              ),
              // $FlowIssue
              <FieldForm
                activeSettings={activeSettings}
                className="row justify-content-center"
                content={content.types[fieldSpecification.name]}
                data={data[fieldName] ? data[fieldName][arrayIndex] : {}}
                debug={debug}
                fieldsClasses={this.props.fieldsClasses}
                formData={formData}
                formGroupClassName={formGroupClassName}
                helpClasses={this.props.helpClasses}
                id={id}
                inputHelps={inputHelps[fieldName] || {}}
                internallyHiddenFields={this.props.internallyHiddenFields}
                isBackOffice={isBackOffice}
                key={`field-form-array-${arrayIndex}`}
                labelClasses={this.props.labelClasses}
                labelValue={`${arrayIndex + 1}`}
                loadedData={loadedData[fieldName] ? loadedData[fieldName][arrayIndex] : {}}
                miniAppData={miniAppData}
                miniApps={miniApps}
                onBlur={(_, value, paths, changed) => {
                  this.handleOnBlur(fieldName, value, paths, changed)
                }}
                onChangeFormData={this.props.onChangeFormData}
                onChangeValue={(key, value, pathArray) => {
                  this.handleOnArrayValueChange(fieldName, arrayIndex, key, value, pathArray)
                }}
                prefix={`${fieldName}-${arrayIndex}-`}
                prefixArray={[...prefixArray, fieldName]}
                pushIsPristine={pushIsPristine}
                resources={resources}
                setInternallyHiddenFields={this.handleChangeFieldVisibility}
                showArrayTypeTitle={showArrayTypeTitle}
                specification={fieldSpecification}
                templateVariables={templateVariables}
                title={arrayTitle.replace('{value}', `${arrayIndex + 1}`)}
                token={token}
                validationErrors={validationErrorsArray}
                valueNames={valueNames}
              />,
            ])
          }
        }
        return (
          specification.fields[fieldName].enabled &&
          !this.state[`isHidden${fieldName}`] &&
          arrayTypes && (
            <div className="mt-3 w-100 mb-3" key={fieldName}>
              {showArrayTypeInput &&
                arrayTypes[fieldName] &&
                (!arrayTypes[fieldName].maxLength || arrayTypes[fieldName].maxLength > 1) && [
                  <NumberInputArrayType
                    arrayTypes={arrayTypes[fieldName]}
                    debug={debug}
                    fieldName={fieldName}
                    key={`row-array-count-${fieldName}-number-input`}
                    label={content.fieldNames[`${fieldName}ArrayCount`] || `undefined: ${fieldName}-array-count`}
                    onChange={this.handleSetNumberOfItemsInArrayType}
                    prefix={prefix}
                    value={arrayTypes[fieldName].length}
                  />,
                ]}
              {miniAppsArray.map((MiniApp, index) => (
                <MiniApp
                  activeSettings={activeSettings}
                  arrayTypes={arrayTypes}
                  content={content.types[fieldSpecification.name]}
                  formData={formData}
                  fullContent={content}
                  fullSpecification={this.props.specification}
                  inputHelps={miniAppInputHelps[index]}
                  isBackOffice={isBackOffice}
                  key={`${prefix}${fieldName}-miniApp-${MiniApp.displayName ||
                    MiniApp.name ||
                    (MiniApp.type && MiniApp.type.name)}`}
                  loadedData={loadedData}
                  miniAppData={miniAppData}
                  onChangeFieldVisibility={this.handleChangeFieldVisibility}
                  onChangeFormData={this.handleChangeFormData}
                  onChangeValue={this.props.onChangeValue}
                  prefix={`${fieldName}-`}
                  pushIsPristine={pushIsPristine}
                  resources={resources}
                  token={token}
                  validationErrors={validationErrors}
                />
              ))}
              {arrayToRender}
            </div>
          )
        )
      } else if (specification.fields[fieldName] && !tabs) {
        const type = specification.fields[fieldName].domain._type
        let additionalProps = {}
        let enabledField = true
        let showLabel = true
        let labelFieldName = fieldName
        let component = type
        let hidden = false
        let required = !!specification.fields[fieldName].required
        let wrapMode
        let hideInputHelp = false
        if (type === 'bool') {
          showLabel = false
          additionalProps.label = content.fieldNames[fieldName]
        }
        if (content.components && content.components[fieldName]) {
          hideInputHelp = content.components[fieldName].hideInputHelp || false
          component = content.components[fieldName].name || type
          hidden = content.components[fieldName].hidden
          showLabel =
            typeof content.components[fieldName].showLabel === 'undefined'
              ? showLabel
              : content.components[fieldName].showLabel
          if (!content.components[fieldName].readOnlyConditions) {
            enabledField = !content.components[fieldName].readOnly
          } else {
            enabledField = !Object.keys(content.components[fieldName].readOnlyConditions).reduce(
              (acc, dataField) =>
                acc &&
                isConditionTrue(
                  fieldName,
                  content.components[fieldName].readOnlyConditions[dataField],
                  data[dataField] || '',
                ),
              true,
            )
          }
          // PG-1682 discount value specific input helps
          if (
            component === 'radioRefSelect' &&
            inputHelps[labelFieldName] &&
            content.components[fieldName].values.some((value) => value in inputHelps[labelFieldName])
          ) {
            additionalProps.inputHelps = { ...inputHelps[labelFieldName] }
          }

          additionalProps = { ...additionalProps, ...content.components[fieldName] }
          delete additionalProps.name
          if (['supplementaryInsuranceSelect', ''].includes(component)) {
            showLabel = false
          }
          if (typeof content.components[fieldName].wrapMode !== 'undefined') {
            wrapMode = content.components[fieldName].wrapMode
          }
          if (content.components[fieldName].associated) {
            const renderedName = this.handleAddAssociatedField(
              content.components[fieldName],
              specification.fields[fieldName],
              content,
            )
            if (renderedName !== fieldName) {
              return <EmptyComponent key={fieldName} />
            } else {
              labelFieldName = content.components[fieldName].parent
              additionalProps.label = content.fieldNames[labelFieldName]
              component = content.components[fieldName].name
              additionalProps.inputHelps = inputHelps
              additionalProps.associatedField =
                associatedFields[content.components[fieldName].parent || 'defaultAssociation']
            }
          }
          if (typeof content.components[fieldName].required === 'boolean') {
            required = content.components[fieldName].required
          }
        }
        let FieldComponent = FormFields.default
        if (FormFields[component]) {
          FieldComponent = FormFields[component]
        } else {
          additionalProps.component = component
        }
        if (content.valueNames && content.valueNames[fieldName]) {
          additionalProps.valueNames = content.valueNames[fieldName]
        }
        if (valueNames && valueNames[fieldName]) {
          additionalProps.valueNames = { ...valueNames[fieldName], ...additionalProps.valueNames }
        }
        formGroupClassName = this.props.formGroupClassName
        if (typeof formGroupClasses[component] === 'string') {
          formGroupClassName = formGroupClasses[component]
        }
        formGroupClassName += ` ${component}-group`

        return [
          specification.fields[fieldName].enabled &&
            !this.state[`isHidden${prefix}${fieldName}`] &&
            !hidden && [
              renderBeforeField && typeof renderBeforeField === 'function'
                ? renderBeforeField(`${prefix}${fieldName}`)
                : null,
              <div className={formGroupClassName} key={`row-${fieldName}-component`}>
                {showLabel && (
                  <div
                    className={
                      labelClassesForm[`${component}${enabledField ? '' : '-disabled'}`] || labelClassesForm[component]
                    }
                  >
                    <FieldLabel
                      id={`${prefix}${fieldName}`}
                      isRequired={required}
                      label={content.fieldNames[labelFieldName]}
                      labelValue={labelValue}
                      wrapMode={wrapMode}
                    />
                  </div>
                )}
                <div
                  className={
                    fieldsClassesForm[`${component}${enabledField ? '' : '-disabled'}`] || fieldsClassesForm[component]
                  }
                >
                  {debug && (
                    <p className="text-secondary">
                      {prefix.replace('-', '.')}
                      {labelFieldName}
                    </p>
                  )}
                  <FieldComponent
                    formData={data}
                    id={`${prefix}${fieldName}`}
                    isPristine={pushIsPristine}
                    loadedData={loadedData[fieldName]}
                    onBlur={this.handleOnBlur}
                    onChange={(value, associatedFieldName) => {
                      this.handleOnValueChange(associatedFieldName || fieldName, value)
                    }}
                    prefix={prefix}
                    resources={resources}
                    templateVariables={templateVariables}
                    validationErrors={validationErrors}
                    value={data ? data[fieldName] : null}
                    {...specification.fields[fieldName]}
                    {...additionalProps}
                    enabled={enabledField}
                  />
                </div>
                {typeof loadedData[fieldName] !== 'undefined' && loadedData[fieldName] !== '' && (
                  <div className="col-1">
                    {loadedData[fieldName] === 'notFound' && <span className="input-icon pfp pfp-meh" />}
                    {loadedData[fieldName] !== 'notFound' && <i className="input-icon pfp pfp-thumbs-up" />}
                  </div>
                )}
                <div className={helpClassesForm[component]}>
                  {inputHelps[labelFieldName] && inputHelps[labelFieldName].content && !hideInputHelp && (
                    <Popover
                      content={inputHelps[labelFieldName].content}
                      id={`ID${id ? `${id}-` : ''}${prefix}${labelFieldName}`}
                      templateVariables={templateVariables}
                      title={inputHelps[labelFieldName].title}
                    />
                  )}
                </div>
              </div>,
            ],
          miniAppsArray.map((MiniApp, index) => (
            <MiniApp
              activeSettings={activeSettings}
              arrayTypes={arrayTypes}
              content={content}
              data={data}
              formData={formData}
              fullContent={content}
              fullSpecification={this.props.specification}
              inputHelps={miniAppInputHelps[index]}
              isBackOffice={isBackOffice}
              key={`${prefix}${fieldName}-miniApp-${MiniApp.displayName ||
                MiniApp.name ||
                (MiniApp.type && MiniApp.type.name)}`}
              loadedData={loadedData}
              miniAppData={miniAppData}
              onChangeFieldVisibility={this.handleChangeFieldVisibility}
              onChangeFormData={this.handleChangeFormData}
              onChangeValue={this.props.onChangeValue}
              prefix={miniAppKey}
              pushIsPristine={pushIsPristine}
              resources={resources}
              specification={specification.fields[fieldName]}
              token={token}
              validationErrors={validationErrors}
            />
          )),
        ]
      }
    })
  }
}
